'use client';

import React, { useCallback, useState } from 'react';
import { SearchModes } from '@/domainLogic/index.ts';
import styled from 'styled-components';
import { Text } from '@lmn/design-system';
import type { SearchParams } from '@/server/searchParams.ts';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import AddElementToLastWordInString from '@/app/common/components/Utility/AddElementToTheEndOfString';
import { areTaxesIncludedInPrice } from '@/app/common/config/configuration/configurationUtilsClient.ts';
import { getSearchParam } from '@/app/common/urlParametersUtils.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import PopoverOrModal from '@/app/common/components/PopoverOrModal';
import usePriceMode from '@/app/components/search/hooks/usePriceMode.ts';
import { Icon } from '@/app/common/icons/Icon';
import { IconWithTooltip } from '../../../../../Tooltip/styled';
import { TooltipType } from '../../../../../Tooltip/TooltipType';
import { TaxesLabel } from './styled';
import type { DealCard } from '../../../../../../utils/buildDealCards/dealCardTypes';
import { replacePlaceholdersWithBoldTags, replacePlaceholdersWithVars } from '../../../../../../utils/translations';
import { replaceVar } from '../../../../../../utils/misc';
import { formatPrice } from '../../../../../../utils/formatting';

// ─── Popover content styles ───────────────────────────────────────────────────

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  margin-bottom: 12px;
`;

const TotalRow = styled(Row)`
  margin-top: 12px;
  margin-bottom: 16px;
`;

const PriceCell = styled.div`
  white-space: nowrap;
  flex-shrink: 0;
`;

const Separator = styled.hr`
  margin: 0;
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.kallisto.color.common.black[100]};
`;

const Explanation = styled(Text).attrs({ variant: 'BodySmall', color: 'textSecondary' })``;

// ─── Props ────────────────────────────────────────────────────────────────────

export type TaxesPopoverProps = {
  taxesText: string | null;
  dealCard: DealCard;
  showTotalPrice: boolean;
  showDoublePrice: boolean;
  travellersCount: number;
  searchParams: SearchParams;
};

// ─── Component ────────────────────────────────────────────────────────────────

const TaxesPopover = ({
  taxesText,
  dealCard,
  showTotalPrice,
  showDoublePrice,
  searchParams,
  travellersCount,
}: TaxesPopoverProps) => {
  const { isMobile } = useCommonClient();
  const { searchInfoData, localization } = usePageWrapperData();
  const { country, configValuesApp } = localization;
  const { t, translateWithElements } = useTranslation();
  const { totalPriceMode } = usePriceMode();
  const showTotalInTooltip = showTotalPrice || showDoublePrice;
  const [triggerEl, setTriggerEl] = useState<HTMLDivElement | null>(null);
  const triggerRef = useCallback((node: HTMLDivElement | null) => setTriggerEl(node), []);
  const searchMode = getSearchParam(searchParams, URLParameters.SEARCH_MODE);
  const isHO = searchMode === SearchModes.HO;
  const isTH = searchMode === SearchModes.TH;
  const { taxes } = dealCard.product.rate.price;
  const { taxesTotal } = dealCard.product.rate.price;
  const { price } = dealCard.product.rate.price;
  const { isPerPerson } = dealCard.product.rate.price;
  const totalPrice = dealCard.product.rate.price.total.price;
  const { nights } = dealCard.product.dates;
  const { rooms: numRooms } = dealCard.product.rate.features;

  // ─── Tax inclusion flags ──────────────────────────────────────────────────

  const taxesIncluded = areTaxesIncludedInPrice({ searchMode: searchInfoData?.searchMode, configValuesApp, country });
  // Double-pricing: DP mode with UK double-pricing config (uses searchInfoData.searchMode, not URL param)
  const isTaxesIncludedDoublePricing =
    searchInfoData.searchMode === SearchModes.DP && configValuesApp?.taxesIncludedDoublePricing;
  const isAllIncluded = isTaxesIncludedDoublePricing || (taxesIncluded && configValuesApp?.taxesIncludedPopup);

  // Show "pp" suffix whenever we're displaying a per-person price (not total)
  const showPpSuffix = !showTotalInTooltip;

  // ─── Price calculations ───────────────────────────────────────────────────

  const fmt = (p: number) => formatPrice({ price: p, localization });

  // Group totals come from the per-person price, not total.price, which memsearch floors to a whole
  // unit for package-discount rates while leaving the per-person price un-floored.
  const useTravellersTotal = isPerPerson && showTotalInTooltip;

  const basePrice = useTravellersTotal ? price * travellersCount : showTotalInTooltip ? totalPrice : price;
  const baseTaxes = useTravellersTotal ? taxes * travellersCount : showTotalInTooltip ? taxesTotal : taxes;

  const priceWithoutTaxes = taxesIncluded ? basePrice - baseTaxes : basePrice;
  const priceWithoutTaxesFormatted = fmt(priceWithoutTaxes);
  const taxesPriceFormatted = fmt(baseTaxes);
  const grandTotalFormatted = fmt(priceWithoutTaxes + baseTaxes);

  // ─── Labels ───────────────────────────────────────────────────────────────

  const taxesTooltipTitle = t('vacation.oss.cards.taxes_tooltip.title');

  const titleKey = isHO
    ? 'vacation.oss.cards.taxes_tooltip.ho_price'
    : isTH
      ? 'vacation.oss.cards.taxes_tooltip.th_price'
      : 'vacation.oss.cards.taxes_tooltip.dp_price';
  const titleLabel = replacePlaceholdersWithBoldTags(t(titleKey));

  const taxesLabelHTML = replacePlaceholdersWithBoldTags(t('vacation.oss.cards.taxes_tooltip.taxes'));

  const nightLabel =
    nights && nights > 1
      ? t('vacation.oss.header.widget.calendar.nights')
      : t('vacation.oss.header.widget.calendar.night');
  const travellerLabel =
    travellersCount > 1
      ? t('vacation.oss.header.widget.people.travellers')
      : t('vacation.oss.header.widget.people.traveller');
  const totalKey =
    numRooms > 1 ? t('vacation.oss.cards.taxes_tooltip.total_rooms') : t('vacation.oss.cards.taxes_tooltip.total');
  const totalLabelHTML = replacePlaceholdersWithBoldTags(
    replaceVar(replacePlaceholdersWithVars(totalKey), {
      ...(nights ? { nights: nights.toString() } : {}),
      nights_label: nightLabel,
      travellers: travellersCount.toString(),
      travellers_label: travellerLabel,
      rooms: numRooms.toString(),
    })
  );

  const explanationSuffix = taxesIncluded ? 'taxes_included' : 'taxes_excluded';
  const explanation = isHO
    ? t(`vacation.oss.cards.taxes_tooltip.ho_explanation_${explanationSuffix}`)
    : t('vacation.oss.cards.taxes_fees_included.all.desc');

  // ─── Popover content ──────────────────────────────────────────────────────

  const pp = showPpSuffix ? <>&nbsp;{t('vacation.oss.cards.pp')}</> : null;

  const content = isAllIncluded ? (
    <>
      <Row>
        <Text variant="Body">{t('vacation.common.flightplushotel')}</Text>
        <PriceCell>
          <Text variant="Body">
            {priceWithoutTaxesFormatted}
            {pp}
          </Text>
        </PriceCell>
      </Row>
      <Row>
        <Text variant="Body">
          {`${t('vacation.common.citytaxes.taxes')} ${t('vacation.common.citytaxes.payablehotel')}`}
        </Text>
        <PriceCell>
          <Text variant="Body">
            {taxesPriceFormatted}
            {pp}
          </Text>
        </PriceCell>
      </Row>
      <Separator />
      <TotalRow>
        <Text variant="Body" weight="bold">
          {t('vacation.common.price.total')}
        </Text>
        <PriceCell>
          <Text variant="Body" weight="bold">
            {grandTotalFormatted}
            {pp}
          </Text>
        </PriceCell>
      </TotalRow>
      <Explanation>{t('vacation.oss.cards.taxes_fees_included.all.desc')}</Explanation>
    </>
  ) : (
    <>
      <Row>
        <Text variant="BodySmall" dangerouslySetInnerHTML={{ __html: titleLabel }} />
        <PriceCell>
          <Text variant="BodySmall">
            {priceWithoutTaxesFormatted}
            {pp}
          </Text>
        </PriceCell>
      </Row>
      <Row>
        <Text variant="BodySmall" dangerouslySetInnerHTML={{ __html: taxesLabelHTML }} />
        <PriceCell>
          <Text variant="BodySmall">
            {taxesPriceFormatted}
            {pp}
          </Text>
        </PriceCell>
      </Row>
      <Separator />
      <TotalRow>
        <Text variant="BodySmall" weight="bold" dangerouslySetInnerHTML={{ __html: totalLabelHTML }} />
        <PriceCell>
          <Text variant="BodySmall" weight="bold">
            {grandTotalFormatted}
            {pp}
          </Text>
        </PriceCell>
      </TotalRow>
      <Explanation>{explanation}</Explanation>
    </>
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  // Legacy double-pricing trigger label: matches `vacation.oss.cards.taxes_double_pricing.includes_label`
  // wiring from the legacy Card so the EVO PriceBottom shows the same copy.
  const rawPrice = dealCard?.product?.rate?.price?.price;
  const rawTaxes = dealCard?.product?.rate?.price?.taxes ?? 0;
  const hasTaxes = rawTaxes > 0;
  const priceWithTaxes = (rawPrice ?? 0) + rawTaxes;
  // With the FT off this keeps its pre-toggler rule: group total whenever double pricing is configured.
  const showLabelAsTravellersTotal = totalPriceMode
    ? showTotalInTooltip
    : (configValuesApp?.doublePriceFamilyEnabled ?? false);
  const showTravellersTotal = isPerPerson && showLabelAsTravellersTotal;
  // In the toggler's total mode the label reuses the amounts the breakdown below is built from, so the
  // trigger and the panel can never show two different totals (server totals are rounded independently
  // of the per-person figures). Everywhere else — including the toggler's per-person mode — the
  // pre-toggler formula stands, so control and the variant's pp mode render identically.
  const useBreakdownAmounts = totalPriceMode && showTotalInTooltip;
  const doublePricingTotalPrice = useBreakdownAmounts
    ? priceWithoutTaxes + baseTaxes
    : showTravellersTotal
      ? priceWithTaxes * travellersCount
      : priceWithTaxes;
  const doublePricingTotalTaxes = useBreakdownAmounts
    ? baseTaxes
    : showTravellersTotal
      ? rawTaxes * travellersCount
      : rawTaxes;
  const doublePricingShowPpSuffix = isPerPerson && !showLabelAsTravellersTotal;
  const doublePricingAmount = fmt(doublePricingTotalPrice);
  const doublePricingTaxes = fmt(doublePricingTotalTaxes);

  const doublePricingLabel = translateWithElements('vacation.oss.cards.taxes_double_pricing.includes_label', {
    amount: (
      <span key="taxes-included-amount" style={{ display: 'inline-flex', alignItems: 'center' }}>
        <strong>{doublePricingAmount}</strong>
        {doublePricingShowPpSuffix && <span>&nbsp;{t('vacation.oss.cards.pp')}</span>}
        <Icon
          category="Semantics"
          name="icon-circle-info"
          size={14}
          color="iconPrimary"
          css="vertical-align: middle; margin-left: 2px;"
        />
      </span>
    ),
    taxes: (
      <span key="taxes-included-taxes">
        {doublePricingTaxes}
        {doublePricingShowPpSuffix && <span>&nbsp;{t('vacation.oss.cards.pp')}</span>}
      </span>
    ),
  });

  if (!hasTaxes) return null;

  return (
    <>
      <TaxesLabel
        ref={triggerRef}
        $variant={isTaxesIncludedDoublePricing ? 'block' : 'inline'}
        $dotted={!isTaxesIncludedDoublePricing && Boolean(configValuesApp?.taxesIncludedPopup)}
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
      >
        {isTaxesIncludedDoublePricing ? (
          doublePricingLabel
        ) : taxesIncluded ? (
          taxesText ? (
            <AddElementToLastWordInString text={taxesText}>
              <IconWithTooltip
                tooltipSize={300}
                tooltipType={TooltipType.primary}
                position="right"
                style={{ marginLeft: 2 }}
              >
                <Icon category="Semantics" name="icon-circle-info" size={16} color="iconPrimary" alt="" />
              </IconWithTooltip>
            </AddElementToLastWordInString>
          ) : (
            // taxesIncluded but no taxesText (taxes value is 0): show the all-in price as trigger
            <>
              {grandTotalFormatted}
              {pp}
              <Icon
                category="Semantics"
                name="icon-circle-info"
                size={14}
                color="iconPrimary"
                css="vertical-align: middle; margin-left: 2px;"
              />
            </>
          )
        ) : (
          taxesText
        )}
      </TaxesLabel>
      <PopoverOrModal
        anchorEl={triggerEl}
        isMobile={isMobile}
        usePortal={false}
        aria-label={taxesTooltipTitle}
        title={taxesTooltipTitle}
        id="taxes-modal"
        bodyBackgroundColor="backgroundPage"
        disableBrowserHistoryNavigation={false}
        align="right"
        width="360px"
      >
        {content}
      </PopoverOrModal>
    </>
  );
};

export default TaxesPopover;
